<template>
  <v-row dense class="lesson-list">
    <v-row v-if="exercise">
      <v-col cols="12">
        <v-row dense>
          <v-col class="shrink" v-if="number">
            <v-avatar color="secondary" size="25">
              <span class="white--text">{{ number }}</span>
            </v-avatar>
          </v-col>
          <v-col class="grow">
            <v-runtime-template class="content column" :template="exercise.exercise.question"/>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" v-if="isAnsweredBySystem">
        <v-alert type="warning">
          Este exercício foi fechado pelo sistema.
        </v-alert>
      </v-col>

      <v-col cols="12" v-if="showYourAnswer">
        <div class="your-answer">
          <v-card outlined flat>
            <v-card-title>
              <v-col cols="12">
                <div class="text-h5 card-content text-center">Sua resposta</div>
              </v-col>
            </v-card-title>
            <v-card-text>
              <v-col cols="12">
                <v-runtime-template v-if="exercise.reply" :template="exercise.reply" class="answer-content"/>
                <nt-audiorecorder v-if="exercise.file_id" :audio-file="fileData()" @recorded="recordedHandle"/>
              </v-col>
            </v-card-text>
          </v-card>
        </div>
      </v-col>

      <v-col cols="12" v-if="showOptions">
        <v-row dense>
          <v-col cols="12" v-for="(option, index) in exercise.exercise.exercise_options" :key="option.id">
            <v-card outlined flat @click.stop="setObjectAnswer(option)">
              <v-card-text>
                <v-row dense>
                  <v-col class="shrink">
                    <v-checkbox v-model="exercise.exercise_option_id" :value="option.id" readonly/>
                  </v-col>
                  <v-col class="grow text-left">
                    <div class="font-weight-bold" v-html="'Alternativa ' + getAlternativeText(index)"></div>
                    <v-runtime-template class="option-description font-weight-medium" :template="option.description"/>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" v-if="exercise.exercise.exercise_tips.length !== 0">
        <div class="exercise-tips">
          <ul>
            <li class="tip" v-for="tip in exercise.exercise.exercise_tips" :key="tip.id">
              <div class="tip-title" v-html="tip.title"></div>
              <div class="tip-description" v-html="tip.description"></div>
            </li>
          </ul>
        </div>
      </v-col>

      <v-col cols="12">
        <div class="answer">
          <div class="answer-form text-center" v-if="showTextArea">
            <div class="answer-upload" v-if="uploadForm.model">
              <nt-mediaupload @newUpload="newFile"/>
              <template v-if="uploadForm.list">
                <v-row dense>
                  <!-- Template para os ítens da listagem -->
                  <nt-medialistitem
                    v-for="(file, i) in files"
                    :key="i"
                    :dataset="file"
                    :event="'manager'"
                    @delete="deleteFile"
                    deleteable/>
                </v-row>
              </template>
            </div>

            <v-textarea outlined hide-details="auto" label="Digite sua resposta aqui." v-else-if="showTextArea" v-model="exercise.reply" rows="7" cols="100" />

            <!-- Mostra este botão apenas quando a variável uploadForm.list for falsa, ou seja, quando a lista
              de arquivos que foram feitos upload estiver vazia -->
            <v-btn depressed class="mt-4" v-if="!uploadForm.list && exercise.exercise.type !== 'DD'" color="warning" ligth dark @click.stop="uploadArea().show()">
              <label v-if="uploadForm.model">Resposta em texto</label>
              <label v-else>Upload Imagem</label>
              <v-icon v-if="!uploadForm.model" right dark>mdi-cloud-upload</v-icon>
            </v-btn>
          </div>

          <div class="answer-multimedia" v-else-if="exercise.exercise.type === 'M' && !exercise.exerciseIsReplied">
            <nt-audiorecorder :audio-file="fileData()" @recorded="recordedHandle"/>
          </div>

          <v-card outlined flat class="mb-4" v-if="exercise.exerciseIsReplied && isObjectiveExercise && exerciseJustification">
            <v-card-title>Justificativa</v-card-title>
            <v-card-text>
              <div v-html="exerciseJustification"></div>
            </v-card-text>
          </v-card>

          <v-card outlined flat v-if="exercise.exerciseIsReplied && (exercise.feedback || exercise.feedback_file_id || exercise.grade || exercise.grade === 0)">
            <v-card-title>Correção</v-card-title>
            <v-card-text v-if="exercise.grade || exercise.grade === 0">
              <strong>Nota:</strong> {{ exercise.grade }}
            </v-card-text>
            <v-card-text v-if="exercise.feedback || exercise.feedback_file_id">
              <strong class="d-block">Considerações do corretor:</strong>
              <v-runtime-template v-if="exercise.feedback" class="card-content" :template="exercise.feedback"/>
              <div class="text-center" v-if="exercise.feedback_file_id">
                <strong>Áudio:</strong>
                <nt-audiorecorder :audio-file="feedbackData()" @recorded="recordedHandle"/>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-col>

      <v-col cols="12" v-if="['OO', 'D'].includes(exercise.exercise.type) && !exercise.exerciseIsReplied && !isAnsweredBySystem">
        <div class="text-center">
          <v-btn
            block
            text
            color="green"
            @click.native="answerExercise()">
            <v-icon left>mdi-cloud-upload</v-icon> Enviar Resposta
          </v-btn>
          <div>
            <small>Clique em <b>enviar resposta</b> para marcar a questão como respondida.</small>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-row>
</template>

<script>
import { SalaDeAula, File, AuthFile } from '@/services/resources'

import VueRuntimeTemplate from '@/components/Utils/VueRuntimeTemplate'
import MediaUpload from '@/components/Media/MediaUpload'
import MediaItem from '@/components/Media/MediaItem'
import AudioRecorder from '@/components/Utils/Audio/AudioRecorder'

const defaultFileForm = () => {
  return {
    id: 0,
    data: null,
    path: ''
  }
}

const defaultUploadForm = () => {
  return {
    model: false,
    list: false
  }
}

export default {
  props: [
    'exerciseProp',
    'number'
  ],
  components: {
    'v-runtime-template': VueRuntimeTemplate,
    'nt-mediaupload': MediaUpload,
    'nt-medialistitem': MediaItem,
    'nt-audiorecorder': AudioRecorder
  },
  data () {
    return {
      totalExercises: 0,
      exercisePosition: 0,
      nextExercise: 0,
      previousExercise: 0,
      exercise: {
        id: 0,
        exercise: {
          id: 0,
          question: '',
          cells_number: 0,
          exercise_tips: [],
          exercise_options: [],
          type: ''
        },
        exercise_id: 0,
        exercise_option_id: 0,
        cells_answers: [],
        instance_id: 0,
        file: defaultFileForm(),
        feedbackFile: defaultFileForm(),
        reply: null,
        feedback: null,
        exerciseIsReplied: false,
        exerciseIsHalfReplied: false,
        answeredCounters: {
          count: 0,
          answered: 0
        }
      },
      uploadForm: defaultUploadForm(),
      files: []
    }
  },
  async mounted () {
    this.exercise = this.exerciseProp
    Object.assign(this.uploadForm, defaultUploadForm())

    switch (this.exercise.exercise.type) {
      case 'OL':
      case 'DL':
        this.exercise.cells_answers = [...Array(this.exercise.exercise.cells_number).keys()]
          .map(i => ({ cell: i, exercise_option_id: null, reply: '' }))
        break

      case 'OM':
        if (!this.exercise.exercise_option_id) {
          this.exercise.exercise_option_id = []
        }
        break

      case 'M':
        if (!this.exercise.file_id) {
          this.exercise.file = { ...defaultFileForm() }
        } else {
          await this.loadRecord(this.exercise.file_id)
        }
        break
    }

    if (!this.exercise.feedback_file_id) {
      this.exercise.feedbackFile = { ...defaultFileForm() }
    } else {
      await this.loadFeedbackRecord(this.exercise.feedback_file_id)
    }
  },
  methods: {
    uploadArea () {
      const vm = this
      return {
        show () {
          vm.uploadForm.model = !vm.uploadForm.model
        },
        showList () {
          vm.uploadForm.list = !vm.uploadForm.list
        }
      }
    },
    setObjectAnswer (exerciseOption) {
      switch (this.exercise.exercise.type) {
        case 'OM':
          if (this.exercise.exercise_option_id.includes(exerciseOption.id)) {
            this.exercise.exercise_option_id.splice(this.exercise.exercise_option_id.indexOf(exerciseOption.id), 1)
          } else {
            this.exercise.exercise_option_id.push(exerciseOption.id)
          }
          break

        default:
          if (this.exercise.exercise_option_id) {
            this.exercise.exercise_option_id = this.exercise.exercise_option_id !== exerciseOption.id ? exerciseOption.id : 0
          } else {
            this.exercise.exercise_option_id = exerciseOption.id
          }
          break
      }
      this.answerExercise()
    },
    getAlternativeText (index) {
      return String.fromCharCode(65 + parseInt(index))
    },
    getExerciseTitleText (index, sum) {
      return parseInt(index) + parseInt(sum)
    },
    setPreviousAndNextExercise () {
      this.totalExercises = Object.keys(this.exerciseList.instances).length
      Object.keys(this.exerciseList.instances).map(index => {
        if (this.exerciseList.instances[index].id === this.exerciseList.nextExercise) {
          this.exercisePosition = index
          this.previousExercise = typeof this.exerciseList.instances[parseInt(index) - 1] !== 'undefined'
            ? this.exerciseList.instances[parseInt(index) - 1].id
            : 0

          this.nextExercise = typeof this.exerciseList.instances[parseInt(index) + 1] !== 'undefined'
            ? this.exerciseList.instances[parseInt(index) + 1].id
            : 0
        }
      })
    },
    isTheOptionCorrect (exerciseOption) {
      return this.exercise.exerciseIsReplied ? exerciseOption.correct : false
    },
    isTheOptionIncorrect (exerciseOption) {
      return this.exercise.exerciseIsReplied ? !exerciseOption.correct : false
    },
    async answerExercise () {
      if (this.exercise.exercise.type === 'M' && !this.exercise.file_id) {
        await this.uploadRecord()
      }

      const data = {
        exercise_list_instance_exercise_id: this.exercise.id,
        exercise_option_id: this.exercise.exercise_option_id,
        reply: this.exercise.reply,
        cells_answers: this.exercise.cells_answers,
        file_id: this.exercise.file_id,
        enrollment_id: parseInt(this.matriculaId)
      }

      SalaDeAula.answerTestExercise(data)
        .then((response) => {
          this.$toasted.global.success({ message: response.body.return.message })
          this.files = []
        })
        .catch((error) => {
          if (error.status === 422 && error.body.errors) {
            const self = this
            Object.values(error.body.errors).forEach((item) => {
              self.$toasted.global.error({ message: item })
            })
          } else if (error.status === 500 || error.status === 412 || error.status === 404) {
            this.$toasted.global.error({ message: error.body.return.message })
          } else {
            this.$toasted.global.error({ message: error.statusText })
          }
        })
    },
    newFile (file) {
      this.files.push(file)
      this.generateReplyByFiles()
      if (!this.uploadForm.list) {
        this.uploadArea().showList()
      }
    },
    deleteFile (file) {
      this.files.forEach((item, key) => {
        if (item.title.localeCompare(file.title) === 0) {
          File.deleteFile(file.title)
            .then((response) => {
              this.$toasted.global.success({ message: response.body.message })
              this.files.splice(key, 1)
              this.$forceUpdate()
              if (this.files.length === 0) {
                this.exercise.reply = null
                this.uploadArea().showList()
              } else {
                this.generateReplyByFiles()
              }
            })
            .catch((error) => {
              if (error.status === 409 || error.status === 503) {
                this.$toasted.global.error({ message: error.body.message })
              } else {
                this.$toasted.global.error({ message: error.statusText })
              }
            })
        }
      })
    },
    generateReplyByFiles () {
      this.exercise.reply = '<ul class="exercise-answer-images">'
      this.files.forEach((item) => {
        this.exercise.reply += '<li class="exercise-answer-item">'
        this.exercise.reply += '<img style="display: block; margin-left: auto; margin-right: auto; max-width: 100%;" height: auto src="/upload/get-file/'
        this.exercise.reply += item.title
        this.exercise.reply += '" alt="" />'
        this.exercise.reply += '</li>'
      })
      this.exercise.reply += '</ul>'
    },
    fileData () {
      return this.exercise.file ? this.exercise.file.data : null
    },
    feedbackData () {
      return this.exercise.feedbackFile ? this.exercise.feedbackFile.data : null
    },
    recordedHandle (fileParam) {
      this.exercise.file.data = fileParam
      this.uploadRecord()
    },
    async uploadRecord () {
      const formData = new FormData()
      formData.append('file', this.exercise.file.data)

      await File.uploadFile(formData)
        .then((response) => {
          this.$toasted.global.success({ message: 'Upload realizado com sucesso!' })
          this.exercise.file_id = response.body.file.id
          this.answerExercise()
        })
        .catch((error) => {
          this.$toasted.global.error({ message: error.body.message })
        })
    },
    async loadRecord (fileId) {
      await AuthFile.getFileById(fileId)
        .then((response) => {
          this.exercise.file.data = response.bodyBlob
          this.$forceUpdate()
        })
        .catch((error) => {
          if (error.status === 404 || error.status === 412) {
            this.$toasted.global.error({ message: error.body.message })
          } else {
            console.log(error)
          }
        })
    },
    async loadFeedbackRecord (fileId) {
      await AuthFile.getFileById(fileId)
        .then((response) => {
          this.exercise.feedbackFile.data = response.bodyBlob
          this.$forceUpdate()
        })
        .catch((error) => {
          if (error.status === 404 || error.status === 412) {
            this.$toasted.global.error({ message: error.body.message })
          } else {
            console.log(error)
          }
        })
    }
  },
  computed: {
    isObjectiveExercise () {
      return this.exercise.exercise.id !== 0
        ? ['O', 'OO', 'OM'].includes(this.exercise.exercise.type)
        : false
    },
    showOptions () {
      return this.exercise.exercise.id !== 0
        ? (['O', 'OM'].includes(this.exercise.exercise.type) ||
          (this.exercise.exercise.type === 'OO' && (this.exercise.exerciseIsHalfReplied ||
            !this.exercise.exerciseIsReplied)))
        : false
    },
    showTextArea () {
      return this.exercise.exercise.id !== 0
        ? ((this.exercise.exercise.type === 'OO' && !this.exercise.exerciseIsHalfReplied && !this.exercise.exerciseIsReplied) ||
          (['DD', 'D'].includes(this.exercise.exercise.type) && !this.exercise.exerciseIsReplied))
        : false
    },
    showYourAnswer () {
      return (this.exercise.exerciseIsReplied &&
          ['D', 'DD', 'OO', 'M', 'OL', 'DL'].includes(this.exercise.exercise.type)) ||
        this.exercise.exerciseIsHalfReplied
    },
    exerciseJustification () {
      let justification = null
      if (this.exercise.exercise.id !== 0) {
        Object.keys(this.exercise.exercise.exercise_options).forEach((item) => {
          justification = this.exercise.exercise.exercise_options[item].correct &&
          this.exercise.exercise.exercise_options[item].justification
            ? this.exercise.exercise.exercise_options[item].justification
            : null
        })
      }
      return justification
    },
    isAnsweredBySystem: {
      get () {
        return !this.exercise.exercise_option_id &&
          !this.exercise.reply &&
          !this.exercise.file_id &&
          this.exercise.grade === 0
      }
    },
    exerciseListStart: {
      get () {
        return new Date(this.exerciseList.created_at).toLocaleString('pt-BR')
      }
    },
    exerciseOptionItems: {
      get () {
        return this.exercise.exercise.exercise_options
          ? this.exercise.exercise.exercise_options.map((option) => ({ text: option.description, value: option.id }))
          : []
      }
    }
  }
}
</script>

<style lang="scss">
  .option-description > p{
    margin-bottom: 0 !important;
  }
</style>
